/**
 * WCC360 — Role name suggestions.
 *
 * Presentation-only lookup over the full career corpus (5,877 roles from the
 * careers bundle). Used by free-text inputs — e.g. pinning a dream role — so
 * people type two letters and pick a real, canonical role title instead of
 * inventing one we can't match later.
 *
 * Nothing here scores or writes. Ranking is a plain string match:
 * exact → prefix → word-start → substring, then shortest title, then A–Z for
 * a stable, deterministic order.
 */

import { loadCareersBundle } from "@/data/careersBundle";

export interface RoleSuggestion {
  name: string;
  family: string | null;
  level: string | null;
}

let cache: RoleSuggestion[] | null = null;
let loading: Promise<RoleSuggestion[]> | null = null;

/** Every role title in the corpus, deduped. Never throws: failure = []. */
export function loadRoleNames(): Promise<RoleSuggestion[]> {
  if (cache) return Promise.resolve(cache);
  if (!loading) {
    loading = loadCareersBundle()
      .then((bundle) => {
        const seen = new Set<string>();
        const out: RoleSuggestion[] = [];
        for (const row of bundle.rows) {
          const name = (row.name ?? "").trim();
          const key = name.toLowerCase();
          if (!name || seen.has(key)) continue;
          seen.add(key);
          out.push({ name, family: row.family ?? null, level: row.level ?? null });
        }
        out.sort((a, b) => a.name.localeCompare(b.name, "en"));
        cache = out;
        return out;
      })
      .catch(() => {
        cache = [];
        return cache;
      });
  }
  return loading;
}

function rank(title: string, q: string): number {
  const t = title.toLowerCase();
  if (t === q) return 0;
  if (t.startsWith(q)) return 1;
  if (new RegExp(`\\b${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`).test(t)) return 2;
  if (t.includes(q)) return 3;
  return -1;
}

/** Best matches for a typed fragment. Empty query = no suggestions. */
export function searchRoles(
  roles: RoleSuggestion[],
  query: string,
  limit = 8,
): RoleSuggestion[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const scored: Array<{ r: RoleSuggestion; s: number }> = [];
  for (const r of roles) {
    const s = rank(r.name, q);
    if (s >= 0) scored.push({ r, s });
  }
  scored.sort(
    (a, b) =>
      a.s - b.s ||
      a.r.name.length - b.r.name.length ||
      a.r.name.localeCompare(b.r.name, "en"),
  );
  return scored.slice(0, limit).map((x) => x.r);
}
